import { createUniqueObjectID, isPrimaryCourseRequest, sortArrayOfObjects } from "../../../js/Helpers";

export const buildRequestSelectionKey = (group_id, student_id) =>
  JSON.stringify([`${group_id}`, `${student_id}`]);

export const parseRequestSelectionKey = (selection_key) => {
  try {
    const parsed_key = JSON.parse(selection_key);
    if (Array.isArray(parsed_key) && parsed_key.length === 2) {
      return { group_id: `${parsed_key[0]}`, student_id: `${parsed_key[1]}` };
    }
  } catch (error) {
    // Fall back to the legacy key format for any in-memory selections.
  }

  const separator_index = `${selection_key}`.lastIndexOf("-");
  if (separator_index === -1) {
    return { group_id: `${selection_key}`, student_id: "" };
  }

  return {
    group_id: `${selection_key}`.slice(0, separator_index),
    student_id: `${selection_key}`.slice(separator_index + 1),
  };
};

export const cloneStudentRequestsForDraft = (students = []) =>
  JSON.parse(JSON.stringify(Array.isArray(students) ? students : []));

export const studentHasActiveCourseRequest = (
  student_info,
  course_id,
  { isInclusion = null } = {},
) => {
  const student_requests = Array.isArray(student_info?.student_requests)
    ? student_info.student_requests
    : [];
  const should_match_inclusion =
    isInclusion === null ? null : isInclusion === true || `${isInclusion}` === "1";

  return student_requests.some((request_group) => {
    const course_requests = Array.isArray(request_group?.course_requests)
      ? request_group.course_requests
      : [];

    return course_requests.some(
      (course_request) =>
        `${course_request?.course_id}` === `${course_id}` &&
        `${course_request?.is_deleted}` === "0" &&
        (should_match_inclusion === null ||
          (`${course_request?.is_inclusion || "0"}` === "1") ===
            should_match_inclusion),
    );
  });
};

export const getStudentsRequestingCourseWithDetails = (students, course_id) => {
  const requesting_students = (Array.isArray(students) ? students : []).reduce(
    (results, student_info) => {
      const student_requests = Array.isArray(student_info.student_requests)
        ? student_info.student_requests
        : [];

      student_requests.forEach((request_group) => {
        const course_requests = Array.isArray(request_group.course_requests)
          ? request_group.course_requests
          : [];

        course_requests.forEach((course_request) => {
          if (
            `${course_request.course_id}` === `${course_id}` &&
            `${course_request.is_deleted}` === "0"
          ) {
            results.push({
              ...student_info,
              request_info: course_request,
              request_group,
              is_primary_request: isPrimaryCourseRequest(course_request, request_group),
              group_id: request_group.group_id,
            });
          }
        });
      });

      return results;
    },
    [],
  );

  return requesting_students.length > 0
    ? sortArrayOfObjects(requesting_students, "last_name", "text", "asc")
    : [];
};

export const extractStudentRequestsForCourse = (students, course_id) =>
  (Array.isArray(students) ? students : []).reduce((results, student_info) => {
    const student_requests = Array.isArray(student_info.student_requests)
      ? student_info.student_requests
      : [];

    student_requests.forEach((request_group) => {
      const course_requests = Array.isArray(request_group.course_requests)
        ? request_group.course_requests
        : [];

      course_requests.forEach((course_request) => {
        if (`${course_request.course_id}` === `${course_id}`) {
          results.push({
            student_id: student_info.student_id,
            group_id: request_group.group_id,
            request_group_priority: request_group.priority || "1",
            group_priority: request_group.priority || "1",
            priority:
              request_group.priority ||
              course_request.priority ||
              "1",
            ...course_request,
          });
        }
      });
    });

    return results;
  }, []);

export const replaceCourseIdInStudentRequests = (
  student_list,
  old_course_id,
  new_course_id,
) => {
  if (!Array.isArray(student_list) || !old_course_id || !new_course_id) {
    return student_list;
  }

  return student_list.map((student_info) => {
    let student_changed = false;
    const student_request_groups = Array.isArray(student_info.student_requests)
      ? student_info.student_requests
      : [];

    const next_request_groups = student_request_groups.map((request_group) => {
      let request_group_changed = false;
      const course_requests = Array.isArray(request_group.course_requests)
        ? request_group.course_requests
        : [];

      const next_course_requests = course_requests.map((course_request) => {
        if (`${course_request.course_id}` !== `${old_course_id}`) {
          return course_request;
        }

        request_group_changed = true;
        student_changed = true;
        return { ...course_request, course_id: new_course_id };
      });

      return request_group_changed
        ? { ...request_group, course_requests: next_course_requests }
        : request_group;
    });

    return student_changed
      ? { ...student_info, student_requests: next_request_groups }
      : student_info;
  });
};

export const addStudentRequestToDraft = (student_list, student_id, course_id) =>
  (Array.isArray(student_list) ? student_list : []).map((student_info) => {
    if (`${student_info.student_id}` !== `${student_id}`) {
      return student_info;
    }

    const student_request_groups = Array.isArray(student_info.student_requests)
      ? student_info.student_requests
      : [];

    let found_matching_request = false;
    const next_request_groups = student_request_groups.map((request_group) => {
      const course_requests = Array.isArray(request_group.course_requests)
        ? request_group.course_requests
        : [];

      const course_request_index = course_requests.findIndex(
        (course_request) =>
          `${course_request.course_id}` === `${course_id}` &&
          `${course_request.is_inclusion || "0"}` !== "1",
      );

      if (course_request_index === -1) {
        return request_group;
      }

      found_matching_request = true;

      const next_course_requests = course_requests.map((course_request) => {
        if (
          `${course_request.course_id}` !== `${course_id}` ||
          `${course_request.is_inclusion || "0"}` === "1"
        ) {
          return course_request;
        }

        return {
          ...course_request,
          is_deleted: "0",
          matched: "0",
          is_inclusion: "0",
        };
      });

      return { ...request_group, course_requests: next_course_requests };
    });

    if (found_matching_request) {
      return { ...student_info, student_requests: next_request_groups };
    }

    const new_group_id = `NEW-${createUniqueObjectID(7)}`;
    return {
      ...student_info,
      student_requests: [
        ...student_request_groups,
        {
          group_id: new_group_id,
          priority: "1",
          course_requests: [
            {
              course_id,
              priority: "1",
              order: "1",
              matched: "0",
              is_inclusion: "0",
              data_origin: "2",
              is_deleted: "0",
            },
          ],
        },
      ],
    };
  });

export const updateDraftCourseRequestField = (
  student_list,
  selection_key,
  course_id,
  field_key,
  field_value,
) => {
  const { group_id, student_id } = parseRequestSelectionKey(selection_key);

  return (Array.isArray(student_list) ? student_list : []).map(
    (student_info) => {
      if (`${student_info.student_id}` !== `${student_id}`) {
        return student_info;
      }

      let student_changed = false;
      const student_request_groups = Array.isArray(student_info.student_requests)
        ? student_info.student_requests
        : [];

      const next_request_groups = student_request_groups.map((request_group) => {
        if (`${request_group.group_id}` !== `${group_id}`) {
          return request_group;
        }

        let request_group_changed = false;
        const course_requests = Array.isArray(request_group.course_requests)
          ? request_group.course_requests
          : [];

        const next_course_requests = course_requests.map((course_request) => {
          if (
            `${course_request.course_id}` !== `${course_id}` ||
            `${course_request.is_deleted}` !== "0"
          ) {
            return course_request;
          }

          request_group_changed = true;
          student_changed = true;

          return {
            ...course_request,
            [field_key]: field_value,
          };
        });

        return request_group_changed
          ? { ...request_group, course_requests: next_course_requests }
          : request_group;
      });

      return student_changed
        ? { ...student_info, student_requests: next_request_groups }
        : student_info;
    },
  );
};

export const removeDraftCourseRequests = (
  student_list,
  selection_keys,
  course_id,
) => {
  const normalized_selection_keys = Array.isArray(selection_keys)
    ? selection_keys
    : [selection_keys];

  const request_lookup = normalized_selection_keys.reduce((results, selection_key) => {
    const { group_id, student_id } = parseRequestSelectionKey(selection_key);
    if (!results[student_id]) {
      results[student_id] = new Set();
    }

    results[student_id].add(`${group_id}`);
    return results;
  }, {});

  return (Array.isArray(student_list) ? student_list : []).map(
    (student_info) => {
      const student_groups_to_remove =
        request_lookup[`${student_info.student_id}`] || null;
      if (!student_groups_to_remove) {
        return student_info;
      }

      let student_changed = false;
      const student_request_groups = Array.isArray(student_info.student_requests)
        ? student_info.student_requests
        : [];

      const next_request_groups = student_request_groups.map((request_group) => {
        if (!student_groups_to_remove.has(`${request_group.group_id}`)) {
          return request_group;
        }

        let request_group_changed = false;
        const course_requests = Array.isArray(request_group.course_requests)
          ? request_group.course_requests
          : [];

        const next_course_requests = course_requests.map((course_request) => {
          if (`${course_request.course_id}` !== `${course_id}`) {
            return course_request;
          }

          request_group_changed = true;
          student_changed = true;
          return { ...course_request, is_deleted: "1" };
        });

        return request_group_changed
          ? { ...request_group, course_requests: next_course_requests }
          : request_group;
      });

      return student_changed
        ? { ...student_info, student_requests: next_request_groups }
        : student_info;
    },
  );
};

export const transferDraftCourseRequests = (
  student_list,
  selection_keys,
  old_course_id,
  new_course_id,
) => {
  const normalized_selection_keys = Array.isArray(selection_keys)
    ? selection_keys
    : [selection_keys];

  const request_lookup = normalized_selection_keys.reduce((results, selection_key) => {
    const { group_id, student_id } = parseRequestSelectionKey(selection_key);
    if (!results[student_id]) {
      results[student_id] = new Set();
    }

    results[student_id].add(`${group_id}`);
    return results;
  }, {});

  return (Array.isArray(student_list) ? student_list : []).map(
    (student_info) => {
      const student_groups_to_transfer =
        request_lookup[`${student_info.student_id}`] || null;
      if (!student_groups_to_transfer) {
        return student_info;
      }

      let student_changed = false;
      const student_request_groups = Array.isArray(student_info.student_requests)
        ? student_info.student_requests
        : [];

      const next_request_groups = student_request_groups.map((request_group) => {
        if (!student_groups_to_transfer.has(`${request_group.group_id}`)) {
          return request_group;
        }

        let request_group_changed = false;
        const course_requests = Array.isArray(request_group.course_requests)
          ? request_group.course_requests
          : [];

        const next_course_requests = course_requests.map((course_request) => {
          if (
            `${course_request.course_id}` !== `${old_course_id}` ||
            `${course_request.is_deleted}` !== "0"
          ) {
            return course_request;
          }

          request_group_changed = true;
          student_changed = true;

          const has_active_target_request = studentHasActiveCourseRequest(
            student_info,
            new_course_id,
            { isInclusion: `${course_request.is_inclusion || "0"}` === "1" },
          );

          if (has_active_target_request) {
            return { ...course_request, is_deleted: "1" };
          }

          return { ...course_request, course_id: new_course_id };
        });

        return request_group_changed
          ? { ...request_group, course_requests: next_course_requests }
          : request_group;
      });

      return student_changed
        ? { ...student_info, student_requests: next_request_groups }
        : student_info;
    },
  );
};

export const duplicateDraftCourseRequests = (
  student_list,
  selection_keys,
  new_course_id,
) => {
  const normalized_selection_keys = Array.isArray(selection_keys)
    ? selection_keys
    : [selection_keys];

  const selected_student_ids = [
    ...new Set(
      normalized_selection_keys.map((selection_key) => {
        const { student_id } = parseRequestSelectionKey(selection_key);
        return `${student_id}`;
      }),
    ),
  ].filter(Boolean);

  return selected_student_ids.reduce(
    (current_students, student_id) =>
      addStudentRequestToDraft(current_students, student_id, new_course_id),
    Array.isArray(student_list) ? student_list : [],
  );
};
